import axios, { AxiosResponse } from 'axios';
import EnvWindow from '../../utils/env';
import { addParam, splitTrueParams, splitParams } from '../../utils/servicesHelper';
import {
  ApproveExamTaskGroupSolutionScoreResponse,
  CloseSampleAssessmentSessionResponse,
  ExamTaskGroupSampleSolutionsResponse,
  ExamTaskGroupSolutionAssignmentState,
  ExamTaskgroupSolutionScoreApprovalCoordinator,
  ExamTaskGroupSolutionScoreStats,
  SampleExamTaskGroupSolutionScoreState,
  SendbackAvailable,
  SendBackSampleResponse,
} from './scoringModel.types';

export async function isExamTaskGroupSolutionAssignedToSampleAssessmentGroup(
  examTaskGroupSolutionId: string,
): Promise<AxiosResponse<ExamTaskGroupSolutionAssignmentState>> {
  return await axios.get(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/examTaskGroupSolutionId/${examTaskGroupSolutionId}/assigned`,
  );
}

export async function assignExamTaskGroupSolutionToSampleAssessmentGroup(
  examTaskGroupSolutionId: string,
): Promise<AxiosResponse<boolean>> {
  return await axios.post(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/examTaskGroupSolutionId/${examTaskGroupSolutionId}`,
  );
}

export async function getExamTaskGroupSolutionScoreStats(): Promise<AxiosResponse<ExamTaskGroupSolutionScoreStats>> {
  return await axios.get(`${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/stats`);
}

export async function getExamTaskSampleSolutionsForExaminer(
  functionId: string,
  functionEnum: string,
  examinerFunctionId: string,
  pageNumber: number,
  pageSize: number,
  signal?: AbortSignal,
  filters?: { [key: string]: string },
  sortByProperty?: string,
  isDescending?: boolean,
  tasksPoints?: any,
): Promise<AxiosResponse<ExamTaskGroupSampleSolutionsResponse>> {
  let params = '';

  if (filters) {
    params += addParam('firstName', filters.firstName ?? ''); //TODO shoudl be fixed to filter "I. Nazwisko" pattern
    params += addParam('lastName', filters.examiner ?? ''); //TODO shoudl be fixed to filter "I. Nazwisko" pattern
    params += addParam('examTaskGroupSolutionScoreId', filters.examTaskGroupSolutionScoreId ?? '');
    params += addParam('schoolId', filters.schoolId ?? '');
    params += splitParams('processType', filters.processType) ?? '';
    params += addParam('pointSumFrom', filters.pointSumFrom ?? '');
    params += addParam('pointSumTo', filters.pointSumTo ?? '');
    params += addParam('isNoAnswer', filters.examTaskNumbersWithNoAnswer ? 'true' : '');
    params += splitParams('progress', filters.progress) ?? '';
    params += splitParams('status', filters.acceptanceStatus) ?? '';
    params += addParam('updateDateFrom', filters.updateDateFrom ?? '');
    params += addParam('updateDateTo', filters.updateDateTo ?? '');
    params += splitTrueParams(filters.alerts ?? '');
  }
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? 'true' : 'false');

  const response = await axios.post(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}?pageSize=${pageSize}&pageNumber=${pageNumber}${params}&examinerId=${functionId}&examinerFunction=${functionEnum}&examinerFunctionId=${examinerFunctionId}`,
    {
      signal: signal ?? undefined,
      tasksPoints
    },
  );
  return response;
}

export async function exportExamTaskSampleSolutionsForExaminerApi(
  sessionName: string,
  signal?: AbortSignal,
  sortByProperty?: string,
  isDescending?: boolean,
  filters?: { [key: string]: string },
  advancedFilterBody?: any
): Promise<Blob> {
  let params = '';

  if (filters) {
    params += addParam('firstName', filters.firstName ?? '');
    params += addParam('lastName', filters.examiner ?? '');
    params += addParam('examTaskGroupSolutionScoreId', filters.examTaskGroupSolutionScoreId ?? '');
    params += addParam('schoolId', filters.schoolId ?? '');
    params += splitParams('processType', filters.processType) ?? '';
    params += addParam('pointSumFrom', filters.pointSumFrom ?? '');
    params += addParam('pointSumTo', filters.pointSumTo ?? '');
    params += splitParams('progress', filters.progress) ?? '';
    params += splitParams('status', filters.acceptanceStatus) ?? '';
    params += addParam('updateDateFrom', filters.updateDateFrom ?? '');
    params += addParam('updateDateTo', filters.updateDateTo ?? '');
    params += addParam(`${filters.alerts}`, filters.alerts ? 'true' : '');
    params += addParam(`sessionName`, sessionName ?? '');
  }
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? 'true' : 'false');

  const response = await axios.post(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/Export?${params}`,
    { signal: signal ?? undefined,
      tasksPoints: advancedFilterBody
     },
  );
  return response.data;
}

export const completeZwoProcess = async (): Promise<AxiosResponse<CloseSampleAssessmentSessionResponse>> => {
  const response = await axios.patch(`${EnvWindow.REACT_APP_SCORING_MODEL__ASSESSMENT_SESSION_API_URL}/closure`);
  return response;
};

export async function getExamTaskGroupSolutionScoreStatus(
  orgzId: string,
): Promise<AxiosResponse<SampleExamTaskGroupSolutionScoreState>> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/status/${orgzId}`,
  );
  return response;
}

export async function approveExamTaskGroupSolutionScore(
  orgzId: string,
  isApproved: boolean,
): Promise<AxiosResponse<SendBackSampleResponse>> {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${orgzId}/approval/${isApproved}`,
  );
  return response;
}

export async function countryCoordinatorApproveExamTaskGroupSolutionScore(
  orgzListId: string[],
  approveAll: boolean,
): Promise<AxiosResponse<ExamTaskgroupSolutionScoreApprovalCoordinator>> {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/multipleApproval/${approveAll}`,
    orgzListId,
  );

  return response;
}

export async function sendbackAvailable(): Promise<SendbackAvailable> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING_MODEL__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/SendBackAvailable`,
  );
  return response.data;
}

export async function revokeExamTaskGroupSolution(examTaskGroupSolutionId: string): Promise<AxiosResponse> {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_SCORING_MODEL_API_URL}/ExamTaskGroupSolution/${examTaskGroupSolutionId}/Revoke`,
  );
  return response;
}
